export interface PendingOperation {
  offlineId: string;
  entityType: string;
  operationType: 'create' | 'update' | 'delete' | 'approve' | 'process';
  recordId?: string;
  payload: Record<string, unknown>;
  createdAt: string;
  createdBy: string;
  userRole: string;
  deviceId: string;
  syncStatus: 'pending' | 'syncing' | 'failed' | 'completed' | 'conflict';
  retryCount: number;
  lastError?: string;
  clientVersion: string;
  dependsOn?: string[];
}

const DB_NAME = 'bts_erp_offline_db';
const DB_VERSION = 1;

export function openOfflineDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains('pending_operations')) {
        db.createObjectStore('pending_operations', { keyPath: 'offlineId' });
      }
      if (!db.objectStoreNames.contains('cached_records')) {
        db.createObjectStore('cached_records', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('sync_history')) {
        db.createObjectStore('sync_history', { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains('sync_conflicts')) {
        db.createObjectStore('sync_conflicts', { keyPath: 'offlineId' });
      }
    };
  });
}

export async function savePendingOperation(op: PendingOperation): Promise<void> {
  const db = await openOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('pending_operations', 'readwrite');
    const store = transaction.objectStore('pending_operations');
    const request = store.put(op);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getPendingOperations(): Promise<PendingOperation[]> {
  const db = await openOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('pending_operations', 'readonly');
    const store = transaction.objectStore('pending_operations');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

export async function removePendingOperation(offlineId: string): Promise<void> {
  const db = await openOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('pending_operations', 'readwrite');
    const store = transaction.objectStore('pending_operations');
    const request = store.delete(offlineId);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function cacheRecordLocally(storeName: string, record: any): Promise<void> {
  const db = await openOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(record);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getCachedRecords(storeName: string): Promise<any[]> {
  const db = await openOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}
